import React, { useRef, useState, useEffect } from 'react';
import { pdf } from '@react-pdf/renderer';
import './TrackingPreview.css'; // Reusing the same CSS
import supabase from '../../supabase';
import InvoicePDF from '../Invoice/InvoicePDF'
import InvoiceQR from '../Invoice/InvoiceQR';
import QrCode from 'qrcode';

export default function TrackingPreview({
  formData,
  onBack,
  calculateSubtotal,
  calculateTax,
  calculateTotal,
}) {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);

  // Fetch user profile and related data
  useEffect(() => {
    (async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (!user || userError) return setLoading(false);

      // Fetch user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('id, company_name, address, gst_number, logo_url, subscription_id, invoice_prefix, email')
        .eq('id', user.id)
        .single();

      if (!profileError && profile) {
        setUserProfile(profile);
        console.log('User profile loaded:', profile); // Debug log
      } else {
        console.error('Error fetching user profile:', profileError);
      }

      // Fetch banks
      const { data: bankList, error: bankError } = await supabase
        .from('banks')
        .select('*')
        .eq('user_id', user.id);
      
      if (!bankError && bankList) {
        setBanks(bankList);
        // Set first bank as default if available
        if (bankList.length > 0) {
          setSelectedBank(bankList[0]);
        }
      }

      // Fetch complete invoice data
      await fetchCompleteInvoiceData(user.id, formData.clientId);
      
      setLoading(false);
    })();
  }, [formData]);

  // Add responsive scaling effect like InvoicePreview
  useEffect(() => {
    const updateScale = () => {
      const container = document.querySelector('.trackingprv-invoice-document');
      if (!container) return;

      const containerWidth = container.parentElement.clientWidth - 40; // 40px for padding
      const documentWidth = 794; // A4 width in pixels (210mm)
      
      let scaleFactor = Math.min(1, containerWidth / documentWidth);
      
      // Ensure minimum readability
      if (scaleFactor < 0.4) scaleFactor = 0.4;
      
      document.documentElement.style.setProperty('--trackingprv-scale-factor', scaleFactor);
      
      // Adjust container height after scaling
      const scaledHeight = container.scrollHeight * scaleFactor;
      container.style.marginBottom = `${(1 - scaleFactor) * scaledHeight}px`;
    };

    updateScale();
    
    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', updateScale);
    
    // Update scale after content loads
    setTimeout(updateScale, 100);
    
    return () => {
      window.removeEventListener('resize', updateScale);
      window.removeEventListener('orientationchange', updateScale);
    };
  }, []);

  const fetchCompleteInvoiceData = async (userId, clientId) => {
    try {
      // Fetch client data
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();

      if (clientError) throw clientError;

      // Prepare invoice data structure
      const invoiceDataStructure = {
        fromInfo: {
          company_name: formData.storeName || formData.fromName || '',
          name: formData.fromName || '',
          address: formData.fromAddress || '',
          phone: formData.fromPhone || '',
          email: formData.fromEmail || '',
          contact_email: formData.fromEmail || '',
          gst_number: formData.fromBusinessNumber || '',
        },
        selectedClient: {
          id: clientId,
          client_name: clientData.client_name,
          client_company_name: clientData.client_company_name,
          address: clientData.address,
          phone: clientData.phone,
          mobile: clientData.mobile,
          fax: clientData.fax,
          email: clientData.client_email,
          client_email: clientData.client_email || clientData.email,
          client_gst_number: clientData.gst_number,
        },
        newClient: null,
        invoiceInfo: {
          number: formData.invoiceNumber,
          issueDate: formData.invoiceDate,
          dueDate: formData.dueDate,
        },
        termsAndConditions: formData.notes ? [formData.notes] : ['Payment due within 30 days'],
        userProfile: null, // Will be set from state
        selectedBank: null, // Will be set from state
      };

      setInvoiceData(invoiceDataStructure);
    } catch (error) {
      console.error('Error fetching complete invoice data:', error);
    }
  };

  // Helper function to get company information from userProfile or fallback to formData
  const getCompanyInfo = () => {
    return {
      name: userProfile?.company_name || formData.storeName || formData.fromName || 'Company Name',
      address: userProfile?.address || formData.fromAddress || '',
      email: userProfile?.email || formData.fromEmail || '',
      gst_number: userProfile?.gst_number || formData.fromBusinessNumber || '',
      phone: formData.fromPhone || '', // Phone might not be in users table, so keep from formData
      logo_url: userProfile?.logo_url || ''
    };
  };

  // Convert number to words with paise support
  const numberToWords = (num) => {
    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
    const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' HUNDRED ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    const convertLargeNumber = (n) => {
      if (n === 0) return '';
      let result = '';
      let crores = Math.floor(n / 10000000);
      n %= 10000000;
      let lakhs = Math.floor(n / 100000);
      n %= 100000;
      let thousands = Math.floor(n / 1000);
      n %= 1000;

      if (crores > 0) result += convertHundreds(crores) + 'CRORE ';
      if (lakhs > 0) result += convertHundreds(lakhs) + 'LAKH ';
      if (thousands > 0) result += convertHundreds(thousands) + 'THOUSAND ';
      if (n > 0) result += convertHundreds(n);

      return result;
    };

    const integerPart = Math.floor(num);
    const decimalPart = Math.round((num - integerPart) * 100);
    let result = integerPart === 0 ? 'ZERO' : convertLargeNumber(integerPart).trim();

    result += ' RUPEES';
    if (decimalPart > 0) {
      result += ' AND ' + convertHundreds(decimalPart).trim() + ' PAISE';
    }
    result += ' ONLY';
    return result;
  };

  // Get total of individual item - FIXED: Use consistent property names
  const getLocalItemTotal = (item) => {
    const qty = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity || 0;
    // Use rate for TrackingPreview calculations
    const price = typeof item.rate === 'string' ? parseFloat(item.rate) || 0 : item.rate || 0;
    return qty * price;
  };

  // FIXED: Function to get item total for InvoicePDF (expects unit_price)
  const getPDFItemTotal = (item) => {
    const qty = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity || 0;
    const price = typeof item.unit_price === 'string' ? parseFloat(item.unit_price) || 0 : item.unit_price || 0;
    return qty * price;
  };

  // Tax on individual item
  const calculateLocalItemTax = (item) => {
    return parseFloat(item.total_tax || 0);
  };

  // Summary of all items
  const calculateLocalSummary = (items) => {
    let subtotal = 0, totalTax = 0, total = 0;
    let sgstTotal = 0, cgstTotal = 0, igstTotal = 0;

    items.forEach(item => {
      if (!item.name) return;
      const base = getLocalItemTotal(item);
      subtotal += base;
      const itemTax = parseFloat(item.total_tax || 0);
      totalTax += itemTax;
      
      // Calculate individual tax components
      const sgstPercent = parseFloat(item.sgst_percent || 0);
      const cgstPercent = parseFloat(item.cgst_percent || 0);
      
      if (sgstPercent > 0) sgstTotal += (base * sgstPercent / 100);
      if (cgstPercent > 0) cgstTotal += (base * cgstPercent / 100);
    });

    total = subtotal + totalTax;

    return { 
      subtotal, 
      totalTax, 
      total, 
      sgstTotal, 
      cgstTotal, 
      igstTotal, 
      discountAmount: 0 
    };
  };

  const generateUPIQRCode = async (upiId, amount, merchantName, invoiceNumber) => {
    const upiString = `upi://pay?pa=${upiId}&am=${amount}&pn=${encodeURIComponent(merchantName)}&tn=${encodeURIComponent(`Invoice ${invoiceNumber}`)}&cu=INR`;
    return await QrCode.toDataURL(upiString, {
      width: 200,
      margin: 1,
      color: { dark: '#000000', light: '#FFFFFF' },
      errorCorrectionLevel: 'M'
    });
  };

  const localSummary = calculateLocalSummary(formData.items || []);
  const hasTax = localSummary.totalTax > 0;
  const showTaxPerItemColumn = hasTax;
  const showHsnColumn = true;
  // Only show value column when there is tax (to show pre-tax amount)
  const showValueColumn = hasTax;

  const generatePDFBlob = async () => {
    if (!invoiceData || !userProfile) {
      throw new Error('Invoice data not ready');
    }

    const companyInfo = getCompanyInfo();
    
    const completeInvoiceData = {
      ...invoiceData,
      userProfile,
      selectedBank,
      // Update fromInfo with user profile data
      fromInfo: {
        ...invoiceData.fromInfo,
        company_name: companyInfo.name,
        address: companyInfo.address,
        email: companyInfo.email,
        contact_email: companyInfo.email,
        gst_number: companyInfo.gst_number,
        phone: companyInfo.phone,
      }
    };

    // FIXED: Transform items to match InvoicePDF expectations with consistent properties
    const itemsWithHsn = (formData.items || []).map(item => {
      const base = getLocalItemTotal(item);
      const itemTax = calculateLocalItemTax(item);
      return {
        // InvoicePDF expects these properties
        product_name: item.name,
        description: item.description,
        hsn_code: item.hsn_sac,
        hsn: item.hsn_sac,
        quantity: item.quantity,
        unit_price: item.rate, // FIXED: Map rate to unit_price for InvoicePDF
        rate_per_item: item.rate,
        sgst_percent: item.sgst_percent,
        cgst_percent: item.cgst_percent,
        total_gst_percent: item.total_gst_percent,
        total_tax: item.total_tax,
        total: item.total,
        // Add calculated values to ensure consistency
        value: base, // This is the pre-tax value
        calculated_total: base + itemTax, // This is the total including tax
      };
    });

    // Generate QR code data URL if needed
    let qrCodeDataURL = null;
    if (selectedBank?.upi_id && localSummary.total < 100001) {
      try {
        qrCodeDataURL = await generateUPIQRCode(
          selectedBank.upi_id,
          localSummary.total.toFixed(2),
          companyInfo.name,
          completeInvoiceData.invoiceInfo.number
        );
      } catch (error) {
        console.error('Error generating QR code:', error);
        qrCodeDataURL = null;
      }
    }

    const doc = (
      <InvoicePDF
        invoiceData={completeInvoiceData}
        items={itemsWithHsn}
        localSummary={localSummary}
        numberToWords={numberToWords}
        getLocalItemTotal={getPDFItemTotal} // FIXED: Use PDF-specific function
        calculateLocalItemTax={calculateLocalItemTax}
        showTaxPerItemColumn={showTaxPerItemColumn}
        showHsnColumn={showHsnColumn}
        showValueColumn={showValueColumn}
        hasTax={hasTax}
        hasdiscount={false}
        discount={0}
        taxType={localSummary.sgstTotal > 0 ? 'SGST_CGST' : 'IGST'}
        sgstRate={0} // You might want to calculate this from items
        cgstRate={0} // You might want to calculate this from items
        igstRate={0} // You might want to calculate this from items
        selectedBank={selectedBank}
        qrCodeDataURL={qrCodeDataURL}
      />
    );

    return await pdf(doc).toBlob();
  };

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const pdfBlob = await generatePDFBlob();
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${formData.invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert(`Error generating PDF: ${err.message}`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  if (!invoiceData) {
    return <div>Error loading invoice data</div>;
  }

  // Get company info for display
  const companyInfo = getCompanyInfo();

  return (
    <div className="trackingprv-invoice-preview-container">
      <div className="trackingprv-invoice-preview-header">
        <h2 className="trackingprv-invoice-preview-title">Invoice Preview</h2>
      </div>

      {/* Invoice Document */}
      <div className="trackingprv-invoice-document">
        {/* Header Section - Company Logo, Company Info and Tax Invoice Badge */}
        <div className="trackingprv-invoice-header">
          <div className="trackingprv-company-section">
            {companyInfo.logo_url && (
              <div className="trackingprv-company-logo">
                <img
                  src={companyInfo.logo_url}
                  alt="Company Logo"
                  className="trackingprv-logo-image"
                  onError={(e) => {
                    console.error("Logo failed to load:", companyInfo.logo_url);
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
            <div className="trackingprv-company-info">
              <div className="trackingprv-company-name">
                {companyInfo.name}
              </div>
              {companyInfo.address && (
                <div className="trackingprv-company-address">
                  {companyInfo.address}
                </div>
              )}
            </div>
          </div>

          <div className="trackingprv-invoice-info-section">
            <div className="trackingprv-tax-invoice-badge">
              TAX INVOICE
            </div>
            <div className="trackingprv-original-copy-text">
              ORIGINAL FOR RECIPIENT
            </div>
          </div>
        </div>

        {/* Three Column Section - Vendor, Client, Invoice Info */}
        <div className="trackingprv-three-column-section">
          {/* Vendor/From Info */}
          <div className="trackingprv-vendor-info-section">
            <div className="trackingprv-section-header">Seller</div>
            <div className="trackingprv-vendor-details">
              <div className="trackingprv-detail-item">
                <span className="trackingprv-detail-label">Name:</span>
                <span className="trackingprv-detail-value">{companyInfo.name}</span>
              </div>
              {companyInfo.address && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Address:</span>
                  <span className="trackingprv-detail-value">{companyInfo.address}</span>
                </div>
              )}
              {companyInfo.phone && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Phone:</span>
                  <span className="trackingprv-detail-value">{companyInfo.phone}</span>
                </div>
              )}
              {companyInfo.email && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Email:</span>
                  <span className="trackingprv-detail-value">{companyInfo.email}</span>
                </div>
              )}
              {companyInfo.gst_number && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">GSTIN:</span>
                  <span className="trackingprv-detail-value">{companyInfo.gst_number}</span>
                </div>
              )}
            </div>
          </div>

          {/* Client Info */}
          <div className="trackingprv-client-info-section">
            <div className="trackingprv-section-header">Buyer</div>
            <div className="trackingprv-client-details">
              <div className="trackingprv-detail-item">
                <span className="trackingprv-detail-label">Name:</span>
                <span className="trackingprv-detail-value">{invoiceData.selectedClient?.client_name || formData.toName || 'Customer Name'}</span>
              </div>
              {invoiceData.selectedClient?.client_company_name && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Company:</span>
                  <span className="trackingprv-detail-value">{invoiceData.selectedClient.client_company_name}</span>
                </div>
              )}
              {(invoiceData.selectedClient?.address || formData.toAddress) && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Address:</span>
                  <span className="trackingprv-detail-value">{invoiceData.selectedClient?.address || formData.toAddress}</span>
                </div>
              )}
              {(invoiceData.selectedClient?.email || invoiceData.selectedClient?.client_email || formData.toEmail) && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Email:</span>
                  <span className="trackingprv-detail-value">{invoiceData.selectedClient?.email || invoiceData.selectedClient?.client_email || formData.toEmail}</span>
                </div>
              )}
              {invoiceData.selectedClient?.phone && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Phone:</span>
                  <span className="trackingprv-detail-value">{invoiceData.selectedClient.phone}</span>
                </div>
              )}
              {invoiceData.selectedClient?.mobile && (
                <div className="trackingprv-detail-item">
                  <span className="trackingprv-detail-label">Mobile:</span>
                  <span className="trackingprv-detail-value">{invoiceData.selectedClient.mobile}</span>
                </div>
              )}
              <div className="trackingprv-detail-item">
                <span className="trackingprv-detail-label">GSTIN:</span>
                <span className="trackingprv-detail-value">{invoiceData.selectedClient?.client_gst_number || '1234'}</span>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="trackingprv-invoice-details-section">
            <div className="trackingprv-invoice-details">
              <div className="trackingprv-detail-row">
                <span className="trackingprv-detail-label">Invoice No.</span>
                <span className="trackingprv-detail-value">{formData.invoiceNumber}</span>
              </div>
              <div className="trackingprv-detail-row">
                <span className="trackingprv-detail-label">Invoice Date</span>
                <span className="trackingprv-detail-value">{formData.invoiceDate || new Date().toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })}</span>
              </div>
              <div className="trackingprv-detail-row">
                <span className="trackingprv-detail-label">Due Date</span>
                <span className="trackingprv-detail-value">{formData.dueDate || new Date().toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="trackingprv-items-table-wrapper">
          <table className="trackingprv-items-table">
            <thead>
              <tr>
                <th className="trackingprv-col-sno">S. No</th>
                <th className="trackingprv-col-description">Name of Product / Service</th>
                {showHsnColumn && <th className="trackingprv-col-hsn">HSN/SAC</th>}
                <th className="trackingprv-col-qty">Qty</th>
                <th className="trackingprv-col-rate">Rate</th>
                {showValueColumn && <th className="trackingprv-col-value">Value</th>}
                {showTaxPerItemColumn && <th className="trackingprv-col-tax">Tax</th>}
                <th className="trackingprv-col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {(formData.items || []).map((item, index) => {
                if (!item.name) return null;
                
                const base = getLocalItemTotal(item);
                const itemTax = calculateLocalItemTax(item);
                const displayQty = parseFloat(item.quantity || 0);
                const displayPrice = parseFloat(item.rate || 0);

                return (
                  <tr key={index} className="trackingprv-item-row">
                    <td className="trackingprv-text-center">{index + 1}</td>
                    <td className="trackingprv-item-description">
                      <div className="trackingprv-product-name">{item.name}</div>
                      {item.description && (
                        <div className="trackingprv-product-desc">{item.description}</div>
                      )}
                    </td>
                    {showHsnColumn && (
                      <td className="trackingprv-text-center">{item.hsn_sac || '-'}</td>
                    )}
                    <td className="trackingprv-text-center">{displayQty}</td>
                    <td className="trackingprv-text-right">{displayPrice.toFixed(2)}</td>
                    {showValueColumn && <td className="trackingprv-text-right">{base.toFixed(2)}</td>}
                    {showTaxPerItemColumn && <td className="trackingprv-text-right">{itemTax.toFixed(2)}</td>}
                    <td className="trackingprv-text-right">{parseFloat(item.total || 0).toFixed(2)}</td>
                  </tr>
                );
              })}

              {/* Empty rows for spacing */}
              {Array.from({ length: Math.max(0, 8 - (formData.items || []).filter(item => item.name).length) }, (_, i) => (
                <tr key={`empty-${i}`} className="trackingprv-empty-row">
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  {showHsnColumn && <td>&nbsp;</td>}
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  {showValueColumn && <td>&nbsp;</td>}
                  {showTaxPerItemColumn && <td>&nbsp;</td>}
                  <td>&nbsp;</td>
                </tr>
              ))}

              {/* Total Row */}
              <tr className="trackingprv-total-row">
                <td colSpan={
                  showHsnColumn && showValueColumn && showTaxPerItemColumn ? "7" :
                  showHsnColumn && showValueColumn ? "6" :
                  showHsnColumn && showTaxPerItemColumn ? "6" :
                  showValueColumn && showTaxPerItemColumn ? "6" :
                  showHsnColumn ? "5" :
                  showValueColumn ? "5" :
                  showTaxPerItemColumn ? "5" : "4"
                } className="trackingprv-total-label">Total</td>
                <td className="trackingprv-total-amount">{localSummary.total.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Amount in Words Section */}
        <div className="trackingprv-total-words">
          <div className="trackingprv-words-label">
            Total in words: <span style={{ fontWeight: 'normal' }}>{numberToWords(localSummary.total)}</span>
          </div>
        </div>

        {/* Updated Bottom Section - Bank Details with QR, Terms, Summary */}
        <div className="trackingprv-invoice-bottom-grid">
          {/* Left Column - Bank Details and Terms */}
          <div className="trackingprv-left-column">            
            {/* Bank Details with integrated QR code */}
            {selectedBank && (
              <div className="trackingprv-bank-details-compact">
                <div className="trackingprv-bank-header-section">
                  {/* Left Column - Bank Information */}
                  <div className="trackingprv-bank-info">
                    <div className="trackingprv-bank-title">
                      BANK DETAILS
                    </div>
                    <div className="trackingprv-bank-content-compact">
                      <span className="trackingprv-bank-label">Bank Name:</span>
                      <span className="trackingprv-bank-value">{selectedBank.bank_name}</span>
                      
                      <span className="trackingprv-bank-label">A/C Number:</span>
                      <span className="trackingprv-bank-value">{selectedBank.account_number}</span>
                      
                      <span className="trackingprv-bank-label">IFSC Code:</span>
                      <span className="trackingprv-bank-value">{selectedBank.ifsc_code}</span>
                      
                      {selectedBank.account_holder_name && (
                        <>
                          <span className="trackingprv-bank-label">A/C Holder:</span>
                          <span className="trackingprv-bank-value">{selectedBank.account_holder_name}</span>
                        </>
                      )}
                      
                      {selectedBank.upi_id && (
                        <>
                          <span className="trackingprv-bank-label">UPI ID:</span>
                          <span className="trackingprv-bank-value">{selectedBank.upi_id}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Column - QR Code */}
                  {selectedBank?.upi_id && (localSummary.total < 100001) && (
                    <div className="trackingprv-bank-qr-code">
                      <div className="trackingprv-qr-container">
                        <InvoiceQR 
                          upiId={selectedBank.upi_id}
                          amount={localSummary.total.toFixed(2)}
                          merchantName={companyInfo.name}
                          invoiceNumber={formData.invoiceNumber}
                          size={92}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Terms and Conditions below bank details */}
            <div className="trackingprv-terms-conditions-compact">
              <div className="trackingprv-terms-title">
                Terms & Conditions
              </div>
              <div className="trackingprv-terms-content">
                {formData.notes ? (
                  <div className="trackingprv-term-item">{formData.notes}</div>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary and Authorized Signatory */}
          <div className="trackingprv-right-column">
            <div className="trackingprv-summary-box">
              <div className="trackingprv-summary-row">
                <span className="trackingprv-summary-label">Taxable Amount</span>
                <span className="trackingprv-summary-value">₹{localSummary.subtotal.toFixed(2)}</span>
              </div>

              {hasTax && localSummary.cgstTotal > 0 && (
                <div className="trackingprv-summary-row">
                  <span className="trackingprv-summary-label">CGST</span>
                  <span className="trackingprv-summary-value">₹{localSummary.cgstTotal.toFixed(2)}</span>
                </div>
              )}

              {hasTax && localSummary.sgstTotal > 0 && (
                <div className="trackingprv-summary-row">
                  <span className="trackingprv-summary-label">SGST</span>
                  <span className="trackingprv-summary-value">₹{localSummary.sgstTotal.toFixed(2)}</span>
                </div>
              )}

              {hasTax && localSummary.igstTotal > 0 && (
                <div className="trackingprv-summary-row">
                  <span className="trackingprv-summary-label">IGST</span>
                  <span className="trackingprv-summary-value">₹{localSummary.igstTotal.toFixed(2)}</span>
                </div>
              )}

              {hasTax && (
                <div className="trackingprv-summary-row">
                  <span className="trackingprv-summary-label">Total Tax</span>
                  <span className="trackingprv-summary-value">₹{localSummary.totalTax.toFixed(2)}</span>
                </div>
              )}

              <div className="trackingprv-summary-row trackingprv-total-after-tax">
                <span className="trackingprv-summary-label">Total Amount After Tax</span>
                <span className="trackingprv-summary-value">₹{localSummary.total.toFixed(2)}</span>
              </div>
            </div>

          
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="trackingprv-invoice-actions">
        <button 
          onClick={onBack} 
          disabled={isGeneratingPDF}
          className="trackingprv-btn-secondary"
        >
          Back & Edit
        </button>
        <button 
          onClick={handleDownloadPDF} 
          disabled={isGeneratingPDF}
          className="trackingprv-btn-primary"
        >
          {isGeneratingPDF ? "Generating..." : "Download PDF"}
        </button>
       
      </div>
    </div>
  );
}